import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, HeadlineM, LabelL } from '@sorare/core/src/atoms/typography';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { getPositionInitials } from '@sorare/core/src/lib/positions';

import type { TopPlayers_AnyGameInterface } from './__generated__/index.graphql';

const StyledPlayerRow = styled(ButtonBase)`
  transition: opacity 0.2s;
`;

const PlayerList = styled(Vertical)`
  &:hover ${StyledPlayerRow} {
    opacity: 0.7;
  }
  &:hover ${StyledPlayerRow}:hover {
    opacity: 1;
  }
`;

const PlayerAvatar = styled.img`
  width: 45px;
  height: 45px;
  object-fit: cover;
  border-radius: 50%;
  outline: 1px solid var(--c-nd-150);
`;

const TeamLogo = styled.img`
  height: var(--double-unit);
`;

type Props = {
  title: ReactNode;

  anyGame: TopPlayers_AnyGameInterface;
  players: TopPlayers_AnyGameInterface['anyPlayers'];
  onPlayerDetailsClick?: (playerSlug: string) => void;
};

export const TopPlayers = ({
  title,
  anyGame,
  players,
  onPlayerDetailsClick,
}: Props) => {
  return (
    <Vertical gap={3}>
      <HeadlineM bold>{title}</HeadlineM>
      <PlayerList gap={1.5}>
        {players.map(player => {
          const cardAvatar = player.lastOwnedCards?.[0]?.pictureUrl;

          return (
            <StyledPlayerRow
              key={`${anyGame.id}-${player.slug}`}
              onClick={() => onPlayerDetailsClick?.(player.slug)}
            >
              <SBHorizontal>
                <Horizontal>
                  <PlayerAvatar
                    src={cardAvatar || player.avatarPictureUrl || ''}
                  />
                  <Vertical gap={0} className="text-left">
                    <LabelL bold>{player.displayName}</LabelL>
                    <Horizontal gap={0.5}>
                      <TeamLogo src={player.activeClub?.pictureUrl || ''} />
                      <BodyS color="var(--c-nd-600)">
                        {player.activeClub?.shortName}
                      </BodyS>
                      <BodyS color="var(--c-nd-600)">
                        <Dot size={0.5} />
                      </BodyS>
                      <BodyS color="var(--c-nd-600)">
                        {player.anyPositions
                          .map(position => getPositionInitials(position))
                          .join(', ')}
                      </BodyS>
                    </Horizontal>
                  </Vertical>
                </Horizontal>
                <Score
                  score={player.playerGameScore?.score ?? 0}
                  scoreStatus={
                    player.playerGameScore?.scoreStatus ?? ScoreStatus.FINAL
                  }
                  shape="PILL"
                  hideReviewing
                />
              </SBHorizontal>
            </StyledPlayerRow>
          );
        })}
      </PlayerList>
    </Vertical>
  );
};

TopPlayers.fragments = {
  AnyGameInterface: gql`
    fragment TopPlayers_AnyGameInterface on AnyGameInterface {
      id
      anyPlayers {
        slug
        displayName
        avatarPictureUrl
        activeClub {
          slug
          shortName
          pictureUrl
        }
        lastOwnedCards(rarities: [limited, rare, super_rare, unique]) {
          slug
          pictureUrl(derivative: "avatar")
        }
        anyPositions
        playerGameScore(gameId: $gameIdWithoutPrefix) {
          id
          score
          scoreStatus
        }
      }
    }
  ` as TypedDocumentNode<TopPlayers_AnyGameInterface>,
};
